<template>
  <v-card outlined class="pa-4 mt-4 ecc-card">
    <div class="font-weight-bold black--text mb-3">
      {{ $t("reports.computeCost.title") }}
    </div>

    <div v-if="loading" class="d-flex align-center">
      <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
      {{ $t("reports.computeCost.calculating") }}
    </div>

    <div v-else class="d-flex flex-wrap" style="gap: 12px 40px">
      <div>
        <div class="text-body-2 mb-1">{{ $t("reports.computeCost.totalRequired") }}</div>
        <div class="font-weight-bold black--text">{{ requiredText }}</div>
      </div>
      <div>
        <div class="text-body-2 mb-1">{{ $t("reports.computeCost.available") }}</div>
        <div class="font-weight-bold black--text">{{ availableText }}</div>
      </div>
      <div>
        <div class="text-body-2 mb-1">{{ $t("reports.computeCost.status") }}</div>
        <div v-if="estimate" class="d-flex align-center">
          <v-icon small :color="estimate.sufficient ? 'green' : 'red'">
            {{ estimate.sufficient ? "mdi-check-circle" : "mdi-alert-circle" }}
          </v-icon>
          <span
            class="font-weight-bold ml-1"
            :class="estimate.sufficient ? 'green--text text--darken-1' : 'red--text'"
          >
            {{ estimate.sufficient ? $t("reports.computeCost.sufficient") : $t("reports.computeCost.insufficient") }}
          </span>
        </div>
        <div v-else class="font-weight-bold">—</div>
      </div>
    </div>
  </v-card>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";

/**
 * Live "Estimated Compute Cost" card for the AI report create screens. Given the
 * report type + selected geofence (+ units for multi-report actions like anomaly
 * history), it previews the credits required, the balance available, and whether
 * the balance covers it. Debounced; only fetches once a geofence is chosen.
 */
export default {
  name: "EstimatedComputeCost",
  props: {
    reportType: { type: String, required: true },
    geofence: { type: Object, default: null },
    units: { type: Number, default: 1 },
  },
  data() {
    return { estimate: null, loading: false, timer: null };
  },
  computed: {
    creditsLabel() {
      return this.$t("reports.computeCost.credits");
    },
    requiredText() {
      if (!this.estimate) return "—";
      return `${this.formatNum(this.estimate.required)} ${this.creditsLabel}`;
    },
    availableText() {
      // Super Admin bypasses credits — available comes back null.
      if (!this.estimate || this.estimate.available == null) return "—";
      return `${this.formatNum(this.estimate.available)} ${this.creditsLabel}`;
    },
  },
  watch: {
    geofence: { deep: true, handler: "schedule" },
    units: "schedule",
    reportType: "schedule",
  },
  created() {
    this.schedule();
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    formatNum(v) {
      return Number(v || 0).toLocaleString();
    },
    schedule() {
      clearTimeout(this.timer);
      if (!this.geofence) {
        this.estimate = null;
        return;
      }
      this.timer = setTimeout(this.fetch, 500);
    },
    async fetch() {
      if (!this.geofence) return;
      this.loading = true;
      try {
        const env = await AiCreditService.estimate({
          reportType: this.reportType,
          geofence: this.geofence,
          units: this.units,
        });
        this.estimate = (env && env.data) || null;
      } catch (e) {
        this.estimate = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ecc-card {
  background: #f4fbf7;
  border-color: #b7e3c9 !important;
}
</style>
